import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api, type Provider } from '../../lib/api';
import { Button } from './Button';

/**
 * The one action after a provider refused: "Reconnect Google" opens the start URL
 * (prompt=consent) in the same tab and the callback returns to the conversation; "Connect
 * another account for mail" (M4.5, no mailbox on the account) opens Connections. The person is
 * never asked to disconnect and reconnect by hand.
 */
export function ReconnectAction({
  provider,
  action = 'reconnect',
  reason,
}: {
  provider: Provider | string;
  action?: 'reconnect' | 'connect' | 'permissions' | undefined;
  /** M5.3: a reconnect asked for a missing permission, not for a refusal. */
  reason?: 'mail_draft_scope' | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const p: Provider = provider === 'microsoft' ? 'microsoft' : 'google';
  const connect = action === 'connect';
  const permissions = action === 'permissions';
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-3 shadow-sm dark:bg-elevated">
      <p className="text-xs text-ink-2" dir="auto">
        {permissions
          ? t('connections.permissionsAction.hint')
          : connect
            ? t('connections.connectAction.hint')
            : reason === 'mail_draft_scope'
              ? t('connections.reconnectAction.reason.mail_draft_scope')
              : t('connections.reconnectAction.hint', {
                  provider: t(`connections.provider.${p}`),
                })}
      </p>
      <Button
        type="button"
        variant="secondary"
        size="md"
        loading={busy}
        onClick={() => {
          if (permissions) {
            navigate('/you/permissions');
            return;
          }
          if (connect) {
            navigate('/you/connections');
            return;
          }
          setBusy(true);
          setFailed(false);
          api
            .startConnection(p, 'two')
            .then(({ url }) => window.location.assign(url))
            .catch(() => {
              setFailed(true);
              setBusy(false);
            });
        }}
      >
        {permissions
          ? t('connections.permissionsAction.label')
          : connect
            ? t('connections.connectAction.label')
            : t(`connections.reconnectAction.${p}`)}
      </Button>
      {failed ? (
        <p className="text-xs text-critical-text" dir="auto">
          {t('common.error')}
        </p>
      ) : null}
    </div>
  );
}
